import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ScheduleSendOffer } from "./send/schedule-send-offer";
import { useTranslation } from "react-i18next";
import { displayAmount, uiLocale } from "../lib/display-amount";
import { editableLocaleAmount, parseLocaleAmount } from "../lib/locale-amount";
import { notify } from "../lib/notify";
import type { Chain, Asset } from "../lib/api";
import { explorerLink, bytesToHex } from "../shared/utils";
import { simulateEvmTransaction, SIMULATION_UNAVAILABLE, type KnownToken, type SimulationResult } from "../lib/txSimulation";
import { simulateSolanaTransaction, unsignedWireTx } from "../lib/tx-simulation-solana";
import { buildSolanaSendMessage } from "./send/sign-solana";
import { useExpertMode } from "../context/ExpertModeContext";
import { SigningError, SigningStepper, signRowLabel, txSigningSteps, TX_PHASE_STEP } from "./tx";
import { fetchPrices, getUsdValue } from "../lib/prices";
import { isTestnetChain } from "../lib/testnet";
import { clearClientKey } from "../lib/mpc";
import { Clock, CircleCheck, Radio, CircleX, Zap, Send } from "lucide-react";
import { authHeaders, isStandaloneJwt } from "../lib/auth";
import { workspaceHeaders } from "../lib/workspace-token";
import { apiUrl } from "../lib/apiBase";
import { usePasskeyPresence } from "./use-passkey-presence";
import { PasskeyGate } from "./PasskeyGate";
import {
  encodeErc20Transfer,
  parseUnits,
  estimateGas,
  getTransactionCount,
} from "../lib/chains/evmTx";
import { fetchEvmGasQuote, evmLevelGasPrices, type EvmGasQuote } from "../lib/chains/evm-gas-price";
import { getChainAdapter } from "../lib/chains/adapter";
import {
  fetchFeeRates,
  mempoolApiUrl,
  type UTXO,
  detectAddressType,
  estimateFee as estimateBtcFee,
} from "../lib/chains/btcTx";
import {
  fetchFeeRates as fetchBchFeeRates,
  estimateFee as estimateBchFee,
} from "../lib/chains/bchTx";
import { fetchDogeFeeRate, estimateDogeFee, DOGE_MIN_FEE_RATE, DOGE_MAX_FEE_RATE } from "../lib/chains/dogeTx";
import {
  fetchFeeRates as fetchLtcFeeRates,
  ltcApiUrl,
  detectAddressType as detectLtcAddressType,
  estimateFee as estimateLtcFee,
} from "../lib/chains/ltcTx";
import { hasSigningShare, resolveSigningKeyFile } from "../lib/signing-key-file";
import { KeyPassphraseCancelled, cancelKeyPassphrase } from "../lib/key-passphrase-prompt";
import { useHideBalances } from "../context/HideBalancesContext";
import { chainUsesEddsa } from "../lib/sign-passkey-authorization";
import { isRecoveryMode } from "../lib/recovery";
import { getPreference } from "../lib/userOverrides";
import { trackExpertTool } from "../lib/expert-usage";
import { getIdentityId } from "../lib/auth";
import { addRecentRecipient } from "../lib/addressBook";
import { resolveName, isResolvableName } from "../lib/nameResolution";
import { checkXlmAccountExists, checkXlmTrustline } from "../lib/chains/xlmTx";
import { xlmDestinationVerdict, type XlmDestinationVerdict } from "../lib/chains/xlm-destination";
import type { KeyFile, FeeLevel, SendStep, SigningPhase, TxResult, SpeedUpData } from "./sendTypes";
import { useSteppedProgress, signingDurationMs, ProgressBar } from "./ProgressBar";
import {
  GAS_LIMIT_NATIVE,
  GAS_LIMIT_ERC20,
  isValidAmount,
  shortAddrPreview,
} from "./sendTypes";
import {
  executeSigningFlow,
  executeBtcSigningFlow,
  executeBchSigningFlow,
  executeLtcSigningFlow,
  executeDogeSigningFlow,
  executeSolanaSigningFlow,
  executeXrpSigningFlow,
  executeXlmSigningFlow,
  executeTronSigningFlow,
  executeTonSigningFlow,
  executeAlgoSigningFlow,
  executeAdaSigningFlow,
  handleFetchUtxos,
  type SigningContext,
  type UtxoFetchContext,
} from "./send/signing-flows";
import { InputStep } from "./send/InputStep";
import { PreviewStep } from "./send/PreviewStep";
import { NetworkChip } from "./review";
import { ResultStep } from "./send/ResultStep";
import { computeFeeDisplay, computeMaxSendable } from "./send/fee-helpers";
import { mapSendError, type SendError } from "./send/send-error";
import { useFirstTimeRecipient } from "./send/use-first-time-recipient";
import { useUtxoMaxSendable } from "./send/use-utxo-max-sendable";
import { feesWithProxyFallback } from "../lib/chains/chain-proxy-utxo";

export function SendDialog({
  keyId,
  asset,
  chain,
  address,
  balance,
  onClose,
  onTxSubmitted,
  onTxConfirmed,
  speedUpData,
}: {
  keyId: string;
  asset: Asset;
  chain: Chain;
  address: string;
  balance: string;
  onClose: () => void;
  onTxSubmitted?: (txHash: string, toAddr: string, amount: string) => void;
  onTxConfirmed?: (txHash: string) => void;
  speedUpData?: SpeedUpData;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hidden: balancesHidden } = useHideBalances();
  const expert = useExpertMode();
  const [step, setStep] = useState<SendStep>("input");

  // Expert mode overrides
  const [nonceOverride, setNonceOverride] = useState("");
  const [gasLimitOverride, setGasLimitOverride] = useState("");
  const [maxFeeOverride, setMaxFeeOverride] = useState("");
  const [priorityFeeOverride, setPriorityFeeOverride] = useState("");
  const [btcFeeRateOverride, setBtcFeeRateOverride] = useState("");
  const [rbfEnabled, setRbfEnabled] = useState(true);
  // getPreference returns default (false) when expert_mode is off
  const confirmBeforeBroadcast = expert && !!getPreference("confirm_before_broadcast", getIdentityId() ?? undefined);

  // UTXO manual selection (expert mode)
  const [showUtxoPicker, setShowUtxoPicker] = useState(false);
  const [availableUtxos, setAvailableUtxos] = useState<UTXO[] | null>(null);
  const [selectedUtxoKeys, setSelectedUtxoKeys] = useState<Set<string>>(new Set());
  const [utxoLoading, setUtxoLoading] = useState(false);

  // Estimated gas and nonce from node
  const [estimatedGas, setEstimatedGas] = useState<bigint | null>(null);
  const [gasEstimateError, setGasEstimateError] = useState<string | null>(null);
  const [currentNonce, setCurrentNonce] = useState<number | null>(null);
  const [to, setTo] = useState(speedUpData?.to ?? "");
  // What the user typed, in the UI language ("1.234,5" in Spanish); `amount` below is its canonical "1234.5".
  const [amountText, setAmountText] = useState(speedUpData ? editableLocaleAmount((Number(speedUpData.amountSats) / 10 ** asset.decimals).toString(), uiLocale()) : "");
  const parsedAmount = parseLocaleAmount(amountText, uiLocale());
  const amount = parsedAmount.kind === "ok" ? parsedAmount.value : "";
  const [toTouched, setToTouched] = useState(false);
  const [amountTouched, setAmountTouched] = useState(false);
  const [showAddrScanner, setShowAddrScanner] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [savingToBook, setSavingToBook] = useState(false);
  const [bookmarkLabel, setBookmarkLabel] = useState("");
  const [resolvedName, setResolvedName] = useState<{ input: string; address: string; source: string } | null>(null);
  const [resolving, setResolving] = useState(false);
  const [evmGasQuote, setEvmGasQuote] = useState<EvmGasQuote | null>(null);
  const evmGasPrices = useMemo(() => (evmGasQuote ? evmLevelGasPrices(evmGasQuote) : null), [evmGasQuote]);
  // The standard-level price: the reference for expert warnings and placeholders.
  const baseGasPrice = evmGasPrices?.medium ?? null;
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [feeLevel, setFeeLevel] = useState<FeeLevel>("medium");

  // BTC-specific state — store all rates
  const [btcFeeRates, setBtcFeeRates] = useState<{ low: number; medium: number; high: number } | null>(null);
  // BCH-specific state
  const [bchFeeRates, setBchFeeRates] = useState<{ low: number; medium: number; high: number } | null>(null);
  // DOGE-specific state: one network rate (sat/byte)
  const [dogeBaseFeeRate, setDogeBaseFeeRate] = useState<number | null>(null);
  // LTC-specific state
  const [ltcFeeRates, setLtcFeeRates] = useState<{ low: number; medium: number; high: number } | null>(null);
  // XLM-specific state (stroops per operation)
  const [xlmFeeRates, setXlmFeeRates] = useState<{ low: number; medium: number; high: number } | null>(null);
  const [xlmDestination, setXlmDestination] = useState<XlmDestinationVerdict | null>(null);
  const [xlmMemo, setXlmMemo] = useState("");
  const [feeCountdown, setFeeCountdown] = useState(10);
  // Last fee quote request failed; feeRetry re-runs the fee effect on "Retry".
  const [feeError, setFeeError] = useState(false);
  const [feeRetry, setFeeRetry] = useState(0);

  // The account's key share is resolved at the sign tap; keyFile is an uploaded file, asked
  // for only when this device holds no share.
  const [keyFile, setKeyFile] = useState<KeyFile | null>(null);
  const [hasStoredShare] = useState(() => hasSigningShare(keyId));
  const signingKeyRef = useRef<KeyFile | null>(null);

  // Backup status — gate large sends (>$100) on backup completion
  // Standalone mode: skip gate (escrow backup is impossible — can't re-auth without key share)
  // Recovery mode: skip gate (the user holds both key files, and the server may be gone or answer without a session)
  const skipBackupGate = isStandaloneJwt() || isRecoveryMode();
  const [hasBackup, setHasBackup] = useState<boolean | null>(skipBackupGate ? true : null);
  const [backupGateError, setBackupGateError] = useState<string | null>(null);
  useEffect(() => {
    if (skipBackupGate) return; // always treat as backed up
    fetch(apiUrl("/api/keys"), { headers: authHeaders() })
      .then((r) => r.json())
      .then(({ keys }) => {
        const key = (keys || []).find((k: { id: string }) => k.id === keyId);
        setHasBackup(key?.hasClientBackup ?? false);
      })
      .catch(() => setHasBackup(null)); // fail-open
  }, [keyId, skipBackupGate]);

  // Signing state
  // XRP destination tag
  const [destinationTag, setDestinationTag] = useState("");

  // Policy pre-check
  const [policyCheck, setPolicyCheck] = useState<{
    allowed: boolean;
    reason?: string;
    blockedLeg?: string | null;
    fraudCheck?: { flagged: boolean; flags: string[]; level: string; address: string };
    /** Blocked only by the daily limit: a scheduled send can pass after the wait. */
    overDailyLimit?: boolean;
  } | null>(null);
  const [policyChecking, setPolicyChecking] = useState(false);
  // What the pre-check was asked: also the description a scheduled send binds.
  const [precheckBody, setPrecheckBody] = useState<Record<string, unknown> | null>(null);

  // Transaction simulation
  const [simResult, setSimResult] = useState<SimulationResult | null>(null);
  const simSeq = useRef(0);

  const [signingPhase, setSigningPhase] = useState<SigningPhase>("loading-keyshare");
  const [signingError, setSigningErrorState] = useState<SendError | null>(null);
  // A cancelled passkey prompt is not an error: back to Review unchanged (send.md step 4).
  function setSigningError(e: SendError | null) {
    if (e?.kind === "cancelled") {
      setSigningErrorState(null);
      setStep("preview");
      return;
    }
    setSigningErrorState(e);
  }
  const [txResult, setTxResult] = useState<TxResult | null>(null);
  const [pendingTxHash, setPendingTxHash] = useState<string | null>(null);
  const [signedRawTx, setSignedRawTx] = useState<string | null>(null);
  const [signatureCount, setSignatureCount] = useState(1);

  // Passkey guard (triggered on confirm, not on dialog open)
  const [passkeyGuard, setPasskeyGuard] = useState<"idle" | "gate">("idle");
  const pendingSignRef = useRef<(() => void) | null>(null);
  const { hasPasskey, markRegistered } = usePasskeyPresence();

  async function guardedSign(action: () => void) {
    if (expert) {
      if (maxFeeOverride || priorityFeeOverride || gasLimitOverride || btcFeeRateOverride) trackExpertTool("send_custom_fee");
      if (nonceOverride) trackExpertTool("send_custom_nonce");
      if (selectedUtxoKeys.size > 0) trackExpertTool("send_manual_utxo");
    }
    // The share that signs: the uploaded file, or this device's share for the account
    // (a passphrase share asks for its passphrase here; a PRF share stays locked).
    try {
      const kf = await resolveSigningKeyFile(keyId, { needsEddsa: chainUsesEddsa(chain.type), uploaded: hasStoredShare ? null : keyFile, owner: passphraseOwner });
      if (!kf) return;
      signingKeyRef.current = kf as KeyFile;
    } catch (err) {
      if (err instanceof KeyPassphraseCancelled) return;
      setStep("signing");
      setSigningError(mapSendError(err, t));
      return;
    }
    // In recovery mode, skip passkey entirely — no server to verify
    if (isRecoveryMode()) {
      action();
      return;
    }
    // The signing step itself asks for the passkey (one tap bound to this transaction's
    // digests, which also unlocks a PRF-encrypted share); only a missing passkey stops here.
    if (await hasPasskey()) {
      action();
    } else {
      pendingSignRef.current = action;
      setPasskeyGuard("gate");
    }
  }

  function onPasskeyComplete(_result?: unknown) {
    markRegistered();
    setPasskeyGuard("idle");
    pendingSignRef.current?.();
    pendingSignRef.current = null;
  }

  // Name resolution (ENS, Unstoppable Domains) with debounce
  useEffect(() => {
    if (!to || !isResolvableName(to)) {
      if (resolvedName && resolvedName.input !== to) setResolvedName(null);
      setResolving(false);
      return;
    }
    if (resolvedName?.input === to) return; // already resolved
    setResolving(true);
    const timer = setTimeout(async () => {
      const result = await resolveName(to, chain.type, chain.rpcUrl);
      if (result) {
        setResolvedName({ input: to, address: result.address, source: result.source });
      } else {
        setResolvedName(null);
      }
      setResolving(false);
    }, 500);
    return () => { clearTimeout(timer); setResolving(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, chain.type, chain.rpcUrl]);

  // The actual address to send to (resolved name address or raw input)
  const effectiveTo = resolvedName?.input === to ? resolvedName.address : to;

  // Track successful sends as recent recipients + notify
  useEffect(() => {
    if (txResult && (txResult.status === "success" || txResult.status === "pending") && to) {
      addRecentRecipient(to, chain.type, asset.symbol);
    }
    if (txResult?.status === "success") {
      notify({
        title: t("notify.txConfirmed"),
        body: `${displayAmount(amount)} ${asset.symbol}`,
        path: `/accounts/${keyId}/${chain.name.toLowerCase()}/${asset.symbol}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txResult?.status]);

  // Pre-fill form for RBF speed-up — intentionally runs once on mount
  useEffect(() => {
    if (!speedUpData) return;
    const utxos: UTXO[] = speedUpData.utxos.map(u => ({
      txid: u.txid, vout: u.vout, value: u.value,
      status: { confirmed: true },
    }));
    setAvailableUtxos(utxos);
    setSelectedUtxoKeys(new Set(utxos.map(u => `${u.txid}:${u.vout}`)));
    setBtcFeeRateOverride(String(speedUpData.minFeeRate));
    setRbfEnabled(true);
    setFeeLevel("high");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Owner of this flow's passphrase prompt: unmounting cancels it (and only it).
  const [passphraseOwner] = useState(() => ({}));
  useEffect(() => () => cancelKeyPassphrase(passphraseOwner), [passphraseOwner]);

  // Clear deserialized key handles from memory when dialog closes or keyFile changes
  useEffect(() => {
    return () => { if (keyFile) clearClientKey(keyFile.id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyFile?.id]);


  // Fetch gas price (EVM) or fee rates (BTC) and USD prices, poll every 10s
  useEffect(() => {
    // Test coins have no value: no fiat amounts on a test network.
    fetchPrices().then((p) => setPrices(isTestnetChain(chain.name) ? {} : p));

    function onFeeFail() { setFeeError(true); }

    function refreshFees() {
      if (chain.type === "btc") {
        const btcApi = mempoolApiUrl(chain.explorerUrl);
        feesWithProxyFallback({ name: chain.name }, () => fetchFeeRates(btcApi))
          .then((rates) => {
            setFeeError(false);
            setBtcFeeRates({
              low: rates.hourFee,
              medium: rates.halfHourFee,
              high: rates.fastestFee,
            });
          })
          .catch(onFeeFail);
      } else if (chain.type === "ltc") {
        const ltcApi = ltcApiUrl(chain.explorerUrl);
        feesWithProxyFallback({ name: chain.name }, () => fetchLtcFeeRates(ltcApi))
          .then((rates) => {
            setFeeError(false);
            setLtcFeeRates({
              low: rates.hourFee,
              medium: rates.halfHourFee,
              high: rates.fastestFee,
            });
          })
          .catch(onFeeFail);
      } else if (chain.type === "bch") {
        fetchBchFeeRates()
          .then((rates) => {
            setFeeError(false);
            setBchFeeRates({
              low: rates.suggested,
              medium: rates.suggested,
              high: rates.suggested,
            });
          })
          .catch(onFeeFail);
      } else if (chain.type === "doge") {
        fetchDogeFeeRate()
          .then((rate) => {
            setFeeError(false);
            setDogeBaseFeeRate(rate);
          })
          .catch(onFeeFail);
      } else if (chain.type === "evm") {
        if (!chain.rpcUrl) return;
        fetchEvmGasQuote(chain.rpcUrl, chain.evmChainId)
          .then((quote) => {
            setFeeError(false);
            setEvmGasQuote(quote);
          })
          .catch(onFeeFail);
      }
      else if (chain.type === "xlm") {
        if (!chain.rpcUrl) return;
        fetch(`${chain.rpcUrl}/fee_stats`)
          .then((r) => r.json())
          .then((data) => {
            const p10 = parseInt(data.fee_charged?.p10 ?? "100", 10);
            const p50 = parseInt(data.fee_charged?.p50 ?? "100", 10);
            const p90 = parseInt(data.fee_charged?.p90 ?? "100", 10);
            setFeeError(false);
            setXlmFeeRates({ low: p10, medium: p50, high: p90 });
          })
          .catch(onFeeFail);
      }
      // Solana / XRP: fixed fee, no polling needed
    }

    refreshFees();
    setFeeCountdown(10);
    const feeIv = setInterval(() => { refreshFees(); setFeeCountdown(10); }, 10_000);
    const countIv = setInterval(() => setFeeCountdown((c) => Math.max(0, c - 1)), 1_000);
    return () => { clearInterval(feeIv); clearInterval(countIv); };
  }, [chain.rpcUrl, chain.explorerUrl, chain.type, chain.name, chain.evmChainId, feeRetry]);

  // Fetch estimated gas and nonce for EVM chains
  useEffect(() => {
    if (chain.type !== "evm" || !chain.rpcUrl || !to || !amount) return;
    const adapter = getChainAdapter(chain.type);
    if (!adapter.isValidAddress(to)) return;

    // Nonce
    getTransactionCount(chain.rpcUrl, address).then(setCurrentNonce).catch(() => {});

    // Gas estimate
    const value = asset.isNative ? "0x" + parseUnits(amount, asset.decimals).toString(16) : "0x0";
    const data = !asset.isNative && asset.contractAddress
      ? "0x" + bytesToHex(encodeErc20Transfer(to, parseUnits(amount, asset.decimals)) as Uint8Array)
      : undefined;
    const txTo = !asset.isNative && asset.contractAddress ? asset.contractAddress : to;
    setGasEstimateError(null);
    estimateGas(chain.rpcUrl, { from: address, to: txTo, value, data })
      .then((gas) => { setEstimatedGas(gas); setGasEstimateError(null); })
      .catch((err) => { setEstimatedGas(null); setGasEstimateError(err?.message || "gas_estimate_failed"); });
  }, [chain.type, chain.rpcUrl, address, to, amount, asset]);

  // Effective fee values based on selected level + expert overrides
  const gasPrice = maxFeeOverride && /^\d+(\.\d+)?$/.test(maxFeeOverride)
    ? BigInt(Math.round(parseFloat(maxFeeOverride) * 1e9))
    : evmGasPrices?.[feeLevel] ?? null;
  const manualUtxos = selectedUtxoKeys.size > 0 && availableUtxos
    ? availableUtxos.filter(u => selectedUtxoKeys.has(`${u.txid}:${u.vout}`))
    : null;
  const utxoInputCount = manualUtxos?.length || 1;
  const effectiveBtcFeeRate = btcFeeRateOverride && /^\d+/.test(btcFeeRateOverride)
    ? parseInt(btcFeeRateOverride)
    : btcFeeRates?.[feeLevel] ?? null;
  const btcFeeRate = effectiveBtcFeeRate;
  const btcEstimatedFee = btcFeeRate != null ? estimateBtcFee(utxoInputCount, btcFeeRate, true, detectAddressType(address)) : null;
  const effectiveLtcFeeRate = btcFeeRateOverride && /^\d+/.test(btcFeeRateOverride)
    ? parseInt(btcFeeRateOverride)
    : ltcFeeRates?.[feeLevel] ?? null;
  const ltcFeeRate = effectiveLtcFeeRate;
  const ltcEstimatedFee = ltcFeeRate != null ? estimateLtcFee(utxoInputCount, ltcFeeRate, true, detectLtcAddressType(address)) : null;
  const effectiveBchFeeRate = btcFeeRateOverride && /^\d+/.test(btcFeeRateOverride)
    ? parseInt(btcFeeRateOverride)
    : bchFeeRates?.[feeLevel] ?? null;
  const bchFeeRate = effectiveBchFeeRate;
  const bchEstimatedFee = bchFeeRate != null ? estimateBchFee(utxoInputCount, bchFeeRate, true) : null;
  // An override outside what buildDogeTransaction accepts is ignored rather than shown and then refused.
  const dogeOverride = btcFeeRateOverride && /^\d+/.test(btcFeeRateOverride) ? parseInt(btcFeeRateOverride) : null;
  const dogeFeeRate = dogeOverride != null && dogeOverride >= DOGE_MIN_FEE_RATE && dogeOverride <= DOGE_MAX_FEE_RATE
    ? dogeOverride
    : dogeBaseFeeRate;
  const dogeEstimatedFee = dogeFeeRate != null ? estimateDogeFee(utxoInputCount, dogeFeeRate, true) : null;

  const defaultGasLimit = estimatedGas ?? (asset.isNative ? GAS_LIMIT_NATIVE : GAS_LIMIT_ERC20);
  const gasLimit = gasLimitOverride && /^\d+$/.test(gasLimitOverride)
    ? BigInt(gasLimitOverride)
    : defaultGasLimit;
  const estimatedFeeWei = gasPrice != null ? gasPrice * gasLimit : null;

  // Unified fee display
  const feeDisplay = computeFeeDisplay({
    chain, asset, feeLevel, prices,
    estimatedFeeWei, gasPrice,
    btcEstimatedFee, btcFeeRate,
    ltcEstimatedFee, ltcFeeRate,
    bchEstimatedFee, bchFeeRate,
    dogeEstimatedFee, dogeFeeRate,
    xlmFeeRates,
  });

  // Validation — use resolved address if name was resolved, otherwise raw input
  const adapter = getChainAdapter(chain.type);
  const toAddr = effectiveTo; // resolved name address or raw input
  const toValid = toAddr.length > 0 && adapter.isValidAddress(toAddr);
  const isNameInput = isResolvableName(to);
  const toError = toTouched && to.length > 0 && !toValid && !resolving && !isNameInput ? t("send.invalidAddress") : null;
  const toSelf = toValid && (chain.type === "solana" ? toAddr === address : toAddr.toLowerCase() === address.toLowerCase());
  const amountCheck = parsedAmount.kind === "ambiguous" ? { valid: false, error: "send.amountAmbiguous" }
    : parsedAmount.kind === "invalid" ? { valid: false, error: "send.amountInvalid" }
    : isValidAmount(amount, balance);
  const amountError = amountTouched && amountText.trim().length > 0 && !amountCheck.valid && amountCheck.error
    ? t(amountCheck.error, { example: editableLocaleAmount("1234.5", uiLocale()) })
    : null;
  const amountUsd = amount ? getUsdValue(amount, asset.symbol, prices) : null;

  const destTagValid = chain.type !== "xrp" || destinationTag === "" || (/^\d+$/.test(destinationTag) && Number(destinationTag) <= 4294967295);
  const canReview = toValid && !toSelf && amountCheck.valid && (keyFile != null || hasStoredShare) && destTagValid;

  // Max sendable: for native tokens, subtract estimated fee from balance
  const utxoMax = useUtxoMaxSendable(chain, asset, address, chain.type === "btc" ? btcFeeRate : chain.type === "ltc" ? ltcFeeRate : chain.type === "doge" ? dogeFeeRate : bchFeeRate, effectiveTo, manualUtxos);
  const maxSendable = computeMaxSendable({
    chain, asset, balance, feeLevel,
    estimatedFeeWei,
    btcEstimatedFee,
    ltcEstimatedFee,
    bchEstimatedFee,
    dogeEstimatedFee,
    xlmFeeRates,
    utxoMaxSats: utxoMax?.sats,
  });
  // Max over more coins than one tx can sign: say that the smallest ones stay behind.
  const maxInputsCapped = utxoMax?.capped && amount === maxSendable ? utxoMax.maxInputs : null;

  const recipientCheck = useFirstTimeRecipient(step === "preview", effectiveTo, address, chain, asset);

  // "Review policy rules": close the send flow and open this wallet's policy rules on /accounts.
  function openPolicyRules() {
    onClose();
    navigate("/accounts", { state: { openPolicyKeyId: keyId } });
  }

  // Failed before anything left this device (build / passkey / MPC signing): "Nothing was sent."
  const nothingSent = signingPhase === "loading-keyshare" || signingPhase === "building-tx" || signingPhase === "mpc-signing";

  const ADDR_PLACEHOLDER: Record<string, string> = { btc: "bc1q...", ltc: "ltc1q...", bch: "bitcoincash:q...", doge: "D...", solana: "So1ana...", evm: "0x" + "0".repeat(40), xrp: "r...", tron: "T..." };
  const placeholder = ADDR_PLACEHOLDER[chain.type] ?? "Address";

  const totalUsd = (() => {
    const sendUsd = amountUsd ?? 0;
    return sendUsd + (feeDisplay.usd ?? 0);
  })();

  /** The share guardedSign resolved, handed to one flow and dropped here (not kept after it). */
  function takeSigningKey(): KeyFile {
    const kf = signingKeyRef.current!;
    signingKeyRef.current = null;
    return kf;
  }

  // Build signing context for delegating to signing-flows module
  function buildSigningCtx(): SigningContext {
    return {
      keyFile: takeSigningKey(),
      chain,
      asset,
      address,
      to: effectiveTo,
      amount,
      onTxSubmitted,
      onTxConfirmed,
      gasPrice,
      gasLimit,
      confirmBeforeBroadcast,
      rbfEnabled,
      btcFeeRate,
      bchFeeRate,
      dogeFeeRate,
      ltcFeeRate,
      manualUtxos,
      destinationTag,
      xlmMemo,
      xlmFeeRates,
      feeLevel,
      setStep,
      setSigningPhase,
      setSignatureCount,
      setSigningError,
      setSignedRawTx,
      setTxResult,
      setPendingTxHash,
      setKeyFile,
      t,
    };
  }

  function buildUtxoFetchCtx(): UtxoFetchContext {
    return { chain, address, setAvailableUtxos, setUtxoLoading };
  }

  // Validating & signing (0) → MPC signing (1) → Broadcast (2) → Confirming (3), as on iOS
  const phaseIndex = TX_PHASE_STEP;

  // Stepped progress: 90% for MPC signing, 10% split across other steps (1s each)
  const stepsAfterMain = (confirmBeforeBroadcast && signingPhase !== "broadcasting" && signingPhase !== "polling") ? 0 : 2;
  const progress = useSteppedProgress(
    step === "signing" ? phaseIndex[signingPhase] : -1,
    1, // main step = MPC signing (index 1)
    stepsAfterMain,
    signingDurationMs(signatureCount),
    false,
  );

  const recovery = isRecoveryMode();
  const signLabel = signRowLabel(t, recovery);
  // Show smooth percentage only during the main MPC signing step
  const signLabelActive = progress.phase === "main"
    ? `${signLabel} ${progress.pct}%`
    : signLabel;
  const phaseLabels: Record<SigningPhase, string> = {
    "loading-keyshare": t("send.validatingAndSigning"),
    "building-tx": t("send.validatingAndSigning"),
    "mpc-signing": signLabelActive,
    "broadcasting": t("send.broadcasting"),
    "polling": t("send.confirming"),
  };

  const isCompact = step === "signing" || step === "result";
  const canClose = step === "input" || step === "preview" || step === "result" || signingError != null
    || (step === "signing" && (signingPhase === "broadcasting" || signingPhase === "polling"))
    || (step === "signing" && (signingPhase === "polling" || signingPhase === "broadcasting"));

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && canClose) onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canClose, onClose]);

  return (
    <div className={`fixed inset-0 z-50 ${isCompact ? "flex items-center justify-center p-4 bg-black/50" : "bg-surface-secondary overflow-y-auto md:bg-transparent md:overflow-hidden md:flex md:items-center md:justify-center md:p-4"}`}>
      {/* Backdrop */}
      {!isCompact && <div className="hidden md:block absolute inset-0 bg-black/50" onClick={canClose ? onClose : undefined} />}
      {isCompact && <div className="absolute inset-0" onClick={canClose ? onClose : undefined} />}

      {/* Dialog — compact popup for signing/result, full-screen for input/preview */}
      <div className={`relative bg-surface-secondary ${isCompact ? "w-full max-w-md rounded-2xl border border-border-primary shadow-xl max-h-[85vh] overflow-hidden" : "min-h-full pb-[env(safe-area-inset-bottom)] md:min-h-0 md:pb-0 md:max-h-[85vh] md:overflow-y-auto md:w-full md:max-w-md md:rounded-2xl md:border md:border-border-primary md:shadow-xl"}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b border-border-secondary shrink-0 ${!isCompact ? "pt-[calc(1rem+env(safe-area-inset-top))] md:pt-4" : ""}`}>
          {step === "preview" ? (
            <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
              <button
                onClick={() => setStep("input")}
                aria-label={t("send.edit")}
                className="p-1 -ml-1 rounded-md text-text-tertiary hover:text-text-primary transition-colors shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h3 className="text-sm font-semibold text-text-primary truncate">{t("send.review")}</h3>
              <span className="ml-auto"><NetworkChip name={chain.displayName} iconUrl={chain.iconUrl} /></span>
            </div>
          ) : step === "signing" ? (
            <h3 className="text-sm font-semibold text-text-primary flex items-center gap-1.5"><Clock className="w-4 h-4" />{t("send.signing")}</h3>
          ) : step === "result" ? (
            <h3 className="text-sm font-semibold text-text-primary">
              {txResult?.status === "success" ? <><CircleCheck className="w-4 h-4 inline-block align-[-2px] mr-1" />{t("send.txSuccess")}</> : txResult?.status === "pending" ? <><Radio className="w-4 h-4 inline-block align-[-2px] mr-1" />{t("send.txBroadcast")}</> : <><CircleX className="w-4 h-4 inline-block align-[-2px] mr-1" />{t("send.txFailed")}</>}
            </h3>
          ) : (
            <h3 className="text-sm font-semibold text-text-primary">
              {speedUpData ? <><Zap className="w-4 h-4 inline-block align-[-2px] mr-1" />{t("send.speedUp", { symbol: asset.symbol })}</> : <><Send className="w-4 h-4 inline-block align-[-2px] mr-1" />{t("send.title", { symbol: asset.symbol })}</>}

            </h3>
          )}
          {canClose && (
            <button
              onClick={onClose}
              className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-tertiary transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {step === "input" && (
          <InputStep
            chain={chain}
            asset={asset}
            address={address}
            balance={balance}
            expert={expert}
            recovery={recovery}
            speedUpData={speedUpData}
            keyFile={keyFile}
            setKeyFile={setKeyFile}
            hasStoredShare={hasStoredShare}
            keyId={keyId}
            to={to}
            setTo={setTo}
            resolving={resolving}
            resolvedName={resolvedName}
            showAddrScanner={showAddrScanner}
            setShowAddrScanner={setShowAddrScanner}
            showSuggestions={showSuggestions}
            setShowSuggestions={setShowSuggestions}
            savingToBook={savingToBook}
            setSavingToBook={setSavingToBook}
            bookmarkLabel={bookmarkLabel}
            setBookmarkLabel={setBookmarkLabel}
            toTouched={toTouched}
            setToTouched={setToTouched}
            toValid={toValid}
            toSelf={toSelf}
            toError={toError}
            placeholder={placeholder}
            destinationTag={destinationTag}
            setDestinationTag={setDestinationTag}
            xlmMemo={xlmMemo}
            setXlmMemo={setXlmMemo}
            amount={amountText}
            setAmount={setAmountText}
            amountTouched={amountTouched}
            setAmountTouched={setAmountTouched}
            amountCheck={amountCheck}
            amountError={amountError}
            amountUsd={amountUsd}
            maxSendable={maxSendable}
            maxInputsCapped={maxInputsCapped}
            balancesHidden={balancesHidden}
            feeLevel={feeLevel}
            setFeeLevel={setFeeLevel}
            feeDisplay={feeDisplay}
            feeCountdown={feeCountdown}
            feeError={feeError}
            onRetryFee={() => { setFeeError(false); setFeeRetry((n) => n + 1); }}
            btcFeeRates={btcFeeRates}
            ltcFeeRates={ltcFeeRates}
            bchFeeRates={bchFeeRates}
            dogeFeeRate={dogeBaseFeeRate}
            baseGasPrice={baseGasPrice}
            evmGasPrices={evmGasPrices}
            gasPrice={gasPrice}
            defaultGasLimit={defaultGasLimit}
            nonceOverride={nonceOverride}
            setNonceOverride={setNonceOverride}
            gasLimitOverride={gasLimitOverride}
            setGasLimitOverride={setGasLimitOverride}
            maxFeeOverride={maxFeeOverride}
            setMaxFeeOverride={setMaxFeeOverride}
            priorityFeeOverride={priorityFeeOverride}
            setPriorityFeeOverride={setPriorityFeeOverride}
            btcFeeRateOverride={btcFeeRateOverride}
            setBtcFeeRateOverride={setBtcFeeRateOverride}
            rbfEnabled={rbfEnabled}
            setRbfEnabled={setRbfEnabled}
            currentNonce={currentNonce}
            showUtxoPicker={showUtxoPicker}
            setShowUtxoPicker={setShowUtxoPicker}
            availableUtxos={availableUtxos}
            selectedUtxoKeys={selectedUtxoKeys}
            setSelectedUtxoKeys={setSelectedUtxoKeys}
            utxoLoading={utxoLoading}
            hasBackup={hasBackup}
            backupGateError={backupGateError}
            setBackupGateError={setBackupGateError}
            totalUsd={totalUsd}
            canReview={canReview}
            policyChecking={policyChecking}
            guardedSign={guardedSign}
            onClose={onClose}
            onPreview={async () => {
              if (chain.type === "xlm" && chain.rpcUrl) {
                setXlmDestination(null);
                const exists = await checkXlmAccountExists(chain.rpcUrl, to);
                const trusts = !asset.isNative && exists && !!asset.contractAddress
                  && await checkXlmTrustline(chain.rpcUrl, to, asset.symbol, asset.contractAddress);
                setXlmDestination(xlmDestinationVerdict({
                  destExists: exists, destTrusts: trusts, isNative: asset.isNative,
                  amountStroops: parseUnits(amount, asset.decimals),
                }));
              }
              if (!isRecoveryMode()) {
                setPolicyChecking(true);
                setPolicyCheck(null);
                try {
                  const baseUnits = parseUnits(amount, asset.decimals).toString();
                  const checkBody = {
                    to: resolvedName?.input === to ? resolvedName.address : to,
                    amount: baseUnits,
                    nativeSymbol: asset.isNative ? asset.symbol : undefined,
                    contractAddress: asset.contractAddress || undefined,
                    chainId: chain.evmChainId ?? undefined,
                    chainType: chain.type,
                    from: address,
                    ...(resolvedName?.input === to ? { resolvedFrom: to, resolvedVia: resolvedName.source } : {}),
                  };
                  setPrecheckBody(checkBody);
                  const res = await fetch(apiUrl(`/api/keys/${keyId}/rules/check`), {
                    method: "POST",
                    headers: { ...(await workspaceHeaders(keyId)), "Content-Type": "application/json" },
                    body: JSON.stringify(checkBody),
                  });
                  if (res.ok) {
                    const result = await res.json();
                    setPolicyCheck(result);
                  }
                } catch {
                  // fail-open
                }
                setPolicyChecking(false);
              }
              // Estimated balance changes (free, any RPC); a failure keeps the static estimate
              setSimResult(null);
              const simId = ++simSeq.current;
              const known: KnownToken[] = asset.isNative ? [] : [{ contractAddress: asset.contractAddress, symbol: asset.symbol, decimals: asset.decimals }];
              const simTo = resolvedName?.input === to && resolvedName.address ? resolvedName.address : to;
              const keepSim = (r: SimulationResult | null) => { if (simId === simSeq.current) setSimResult(r ?? SIMULATION_UNAVAILABLE); };
              if (chain.type === "evm" && chain.rpcUrl) {
                const value = asset.isNative ? "0x" + parseUnits(amount, asset.decimals).toString(16) : "0x0";
                const dataBytes = !asset.isNative && asset.contractAddress
                  ? encodeErc20Transfer(simTo, parseUnits(amount, asset.decimals))
                  : null;
                const data = dataBytes ? "0x" + bytesToHex(dataBytes as Uint8Array) : "0x";
                const txTo = !asset.isNative && asset.contractAddress ? asset.contractAddress : simTo;
                simulateEvmTransaction(chain.rpcUrl, { from: address, to: txTo, value, data }, {
                  nativeSymbol: asset.isNative ? asset.symbol : undefined, knownTokens: known,
                }).then(keepSim);
              } else if (chain.type === "solana" && chain.rpcUrl) {
                const rpc = chain.rpcUrl;
                buildSolanaSendMessage(rpc, address, simTo, asset, amount)
                  .then((msg) => simulateSolanaTransaction(rpc, unsignedWireTx(msg), address, known))
                  .then(keepSim, () => keepSim(null));
              }
              if (resolvedName?.input === to && resolvedName.address) {
                setTo(resolvedName.address);
              }
              setStep("preview");
            }}
            t={t}
            handleFetchUtxosCallback={() => handleFetchUtxos(buildUtxoFetchCtx())}
          />
        )}

        {step === "preview" && (
          <PreviewStep
            chain={chain}
            asset={asset}
            address={address}
            to={effectiveTo}
            amount={amount}
            expert={expert}
            feeDisplay={feeDisplay}
            gasLimit={gasLimit}
            gasLimitOverride={gasLimitOverride}
            maxFeeOverride={maxFeeOverride}
            estimatedGas={estimatedGas}
            baseGasPrice={baseGasPrice}
            gasEstimateError={gasEstimateError}
            estimatedFeeWei={estimatedFeeWei}
            xlmDestination={xlmDestination}
            destinationTag={destinationTag}
            xlmMemo={xlmMemo}
            policyCheck={policyCheck}
            simResult={simResult}
            manualUtxos={manualUtxos}
            rbfEnabled={rbfEnabled}
            currentNonce={currentNonce}
            totalUsd={totalUsd}
            amountUsd={amountUsd}
            balancesHidden={balancesHidden}
            btcEstimatedFee={btcEstimatedFee}
            ltcEstimatedFee={ltcEstimatedFee}
            bchEstimatedFee={bchEstimatedFee}
            dogeEstimatedFee={dogeEstimatedFee}
            prices={prices}
            balance={balance}
            setStep={setStep}
            guardedSign={guardedSign}
            signingFlows={{
              executeEvm: () => executeSigningFlow(buildSigningCtx()),
              executeBtc: () => executeBtcSigningFlow(buildSigningCtx()),
              executeLtc: () => executeLtcSigningFlow(buildSigningCtx()),
              executeBch: () => executeBchSigningFlow(buildSigningCtx()),
              executeDoge: () => executeDogeSigningFlow(buildSigningCtx()),
              executeSolana: () => executeSolanaSigningFlow(buildSigningCtx()),
              executeXrp: () => executeXrpSigningFlow(buildSigningCtx()),
              executeXlm: () => executeXlmSigningFlow(buildSigningCtx()),
              executeTron: () => executeTronSigningFlow(buildSigningCtx()),
              executeTon: () => executeTonSigningFlow(buildSigningCtx()),
              executeAlgo: () => executeAlgoSigningFlow(buildSigningCtx()),
              executeAda: () => executeAdaSigningFlow(buildSigningCtx()),
            }}
            setSigningError={setSigningError}
            confirmBeforeBroadcast={confirmBeforeBroadcast}
            firstTimeRecipient={recipientCheck.firstTime}
            knownAddresses={recipientCheck.known}
            onReviewPolicy={isRecoveryMode() ? undefined : openPolicyRules}
            t={t}
          />
        )}
        {step === "preview" && policyCheck?.allowed === false && policyCheck.overDailyLimit && !policyCheck.fraudCheck?.flagged && precheckBody && !isRecoveryMode() && (
          <ScheduleSendOffer keyId={keyId} body={precheckBody} />
        )}

        {step === "signing" && (
          <div className="p-5">
            {signingError ? (
              <SigningError
                error={signingError.message}
                detail={signingError.detail}
                policyBlocked={signingError.kind === "policy"}
                onReviewPolicy={signingError.kind === "policy" && !recovery ? openPolicyRules : undefined}
                nothingSent={nothingSent}
                onClose={onClose}
                onRetry={() => { setSigningError(null); setStep("preview"); }}
              />
            ) : (
              <div className="py-6">
                <p className="text-sm font-medium text-text-primary text-center mb-2">
                  {phaseLabels[signingPhase]}
                </p>
                <p className="text-[11px] text-text-muted text-center mb-4">
                  {t("send.signingLine", { amount: displayAmount(amount), symbol: asset.symbol, to: shortAddrPreview(to) })}
                </p>
                <div className="mb-5">
                  <ProgressBar {...progress} minimal={!expert} />
                </div>
                <SigningStepper
                  steps={txSigningSteps(t, signLabelActive, confirmBeforeBroadcast && signingPhase !== "broadcasting" && signingPhase !== "polling")}
                  currentIndex={phaseIndex[signingPhase]}
                />
                {pendingTxHash && signingPhase === "polling" && (
                  <a
                    href={explorerLink(chain.explorerUrl, `/tx/${pendingTxHash}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between mt-5 mx-auto max-w-[260px] px-3 py-2.5 rounded-lg bg-surface-primary/60 border border-border-secondary hover:border-blue-500/30 transition-colors group"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[11px] text-text-muted shrink-0">{t("send.txHash")}</span>
                      <span className="text-xs font-mono text-text-secondary truncate">{shortAddrPreview(pendingTxHash)}</span>
                    </div>
                    <svg className="w-3.5 h-3.5 text-text-muted group-hover:text-blue-400 shrink-0 ml-2 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {step === "result" && txResult && (
          <ResultStep
            chain={chain}
            asset={asset}
            to={effectiveTo}
            amount={amount}
            txResult={txResult}
            signedRawTx={signedRawTx}
            confirmBeforeBroadcast={confirmBeforeBroadcast}
            onClose={onClose}
            onTxSubmitted={onTxSubmitted}
            onTxConfirmed={onTxConfirmed}
            setSignedRawTx={setSignedRawTx}
            setTxResult={setTxResult}
            setStep={setStep}
            setSigningPhase={setSigningPhase}
            setSigningError={setSigningError}
            setPendingTxHash={setPendingTxHash}
            t={t}
          />
        )}
      </div>

      {/* Passkey guard dialogs */}
      {passkeyGuard === "gate" && (
        <PasskeyGate
          onRegistered={onPasskeyComplete}
          onCancel={() => { setPasskeyGuard("idle"); pendingSignRef.current = null; signingKeyRef.current = null; }}
        />
      )}
    </div>
  );
}
